/**
 * Entité Transaction - Représente une transaction bancaire
 *
 * Règles métier CRITIQUES:
 * - IMMUTABLE: Une transaction validée ne peut JAMAIS être modifiée
 * - Créée uniquement via factory method Transaction.create()
 * - ID et références générés automatiquement
 * - Pas de setters
 * - Object.freeze() pour garantir l'immutabilité
 */

import { Money } from '../value-objects/Money';
import { TransactionId } from '../value-objects/TransactionId';
import { AccountNumber } from '../value-objects/AccountNumber';

export enum TransactionType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export enum TransactionStatus {
  COMPLETED = 'COMPLETED',
  // Pas de CANCELLED - une transaction validée est immutable
  // Si erreur, créer une transaction compensatoire
}

export interface TransactionProps {
  readonly id: TransactionId;
  readonly type: TransactionType;
  readonly amount: Money;
  readonly accountNumber: AccountNumber;
  readonly clientId: string;
  readonly clientName: string;
  readonly cashierId: string;
  readonly cashierName: string;
  readonly createdAt: Date;
  readonly reference: string;
  readonly biotransacRef: string;
  readonly status: TransactionStatus;
  readonly notes?: string;
  // Pour les retraits
  readonly biometricAuthId?: string;
  readonly identityVerificationId?: string;
  readonly denomination?: Map<number, number>; // coupure -> quantité
  readonly destination?: string; // Pour les retraits: usage des fonds
  // Pour les versements
  readonly depositorIsThirdParty?: boolean;
  readonly thirdPartyInfo?: {
    name: string;
    idType: string;
    idNumber: string;
    contact: string;
    relation?: string;
  };
}

export class Transaction {
  private constructor(private readonly props: TransactionProps) {
    // Geler l'objet pour garantir l'immutabilité
    Object.freeze(this.props);
    Object.freeze(this);
  }

  /**
   * Factory method - SEUL moyen de créer une transaction
   */
  static create(
    props: Omit<TransactionProps, 'id' | 'createdAt' | 'reference' | 'biotransacRef' | 'status'>
  ): Transaction {
    const id = TransactionId.generate();
    const reference = Transaction.generateReference(props.type);
    const biotransacRef = Transaction.generateBiotransacRef();
    const createdAt = new Date();

    return new Transaction({
      ...props,
      id,
      createdAt,
      reference,
      biotransacRef,
      status: TransactionStatus.COMPLETED,
    });
  }

  /**
   * Reconstitution depuis persistence (pas de validation)
   * Utilisé uniquement par les repositories
   */
  static reconstitute(props: TransactionProps): Transaction {
    return new Transaction(props);
  }

  /**
   * Génère une référence unique pour la transaction
   * Format: RTR-YYYYMMDD-XXXX (retrait) ou VRS-YYYYMMDD-XXXX (versement)
   */
  private static generateReference(type: TransactionType): string {
    const prefix = type === TransactionType.WITHDRAWAL ? 'RTR' : 'VRS';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${prefix}-${date}-${random}`;
  }

  /**
   * Génère une référence Biotransac unique
   * Format: BT-XXXXXX
   */
  private static generateBiotransacRef(): string {
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    return `BT-${random}`;
  }

  // Getters (pas de setters - immutable)
  get id(): TransactionId {
    return this.props.id;
  }

  get type(): TransactionType {
    return this.props.type;
  }

  get amount(): Money {
    return this.props.amount;
  }

  get accountNumber(): AccountNumber {
    return this.props.accountNumber;
  }

  get clientId(): string {
    return this.props.clientId;
  }

  get clientName(): string {
    return this.props.clientName;
  }

  get cashierId(): string {
    return this.props.cashierId;
  }

  get cashierName(): string {
    return this.props.cashierName;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get reference(): string {
    return this.props.reference;
  }

  get biotransacRef(): string {
    return this.props.biotransacRef;
  }

  get status(): TransactionStatus {
    return this.props.status;
  }

  get notes(): string | undefined {
    return this.props.notes;
  }

  get biometricAuthId(): string | undefined {
    return this.props.biometricAuthId;
  }

  get identityVerificationId(): string | undefined {
    return this.props.identityVerificationId;
  }

  get denomination(): Map<number, number> | undefined {
    return this.props.denomination;
  }

  get destination(): string | undefined {
    return this.props.destination;
  }

  get depositorIsThirdParty(): boolean | undefined {
    return this.props.depositorIsThirdParty;
  }

  get thirdPartyInfo(): TransactionProps['thirdPartyInfo'] {
    return this.props.thirdPartyInfo;
  }

  /**
   * Vérifie si c'est un retrait
   */
  isWithdrawal(): boolean {
    return this.props.type === TransactionType.WITHDRAWAL;
  }

  /**
   * Vérifie si c'est un versement
   */
  isDeposit(): boolean {
    return this.props.type === TransactionType.DEPOSIT;
  }

  /**
   * Vérifie si la transaction est complétée
   */
  isCompleted(): boolean {
    return this.props.status === TransactionStatus.COMPLETED;
  }

  /**
   * Compare deux transactions (par ID)
   */
  equals(other: Transaction): boolean {
    return this.props.id.equals(other.props.id);
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): Record<string, unknown> {
    return {
      id: this.props.id.toJSON(),
      type: this.props.type,
      amount: this.props.amount.toJSON(),
      accountNumber: this.props.accountNumber.toJSON(),
      clientId: this.props.clientId,
      clientName: this.props.clientName,
      cashierId: this.props.cashierId,
      cashierName: this.props.cashierName,
      createdAt: this.props.createdAt.toISOString(),
      reference: this.props.reference,
      biotransacRef: this.props.biotransacRef,
      status: this.props.status,
      notes: this.props.notes,
      biometricAuthId: this.props.biometricAuthId,
      identityVerificationId: this.props.identityVerificationId,
      denomination: this.props.denomination
        ? Array.from(this.props.denomination.entries())
        : undefined,
      destination: this.props.destination,
      depositorIsThirdParty: this.props.depositorIsThirdParty,
      thirdPartyInfo: this.props.thirdPartyInfo,
    };
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(json: Record<string, any>): Transaction {
    return Transaction.reconstitute({
      id: TransactionId.fromJSON(json.id),
      type: json.type as TransactionType,
      amount: Money.fromJSON(json.amount),
      accountNumber: AccountNumber.fromJSON(json.accountNumber),
      clientId: json.clientId,
      clientName: json.clientName,
      cashierId: json.cashierId,
      cashierName: json.cashierName,
      createdAt: new Date(json.createdAt),
      reference: json.reference,
      biotransacRef: json.biotransacRef,
      status: json.status as TransactionStatus,
      notes: json.notes,
      biometricAuthId: json.biometricAuthId,
      identityVerificationId: json.identityVerificationId,
      denomination: json.denomination
        ? new Map(json.denomination)
        : undefined,
      destination: json.destination,
      depositorIsThirdParty: json.depositorIsThirdParty,
      thirdPartyInfo: json.thirdPartyInfo,
    });
  }
}
